/**
 * AppHeader — shared two-row header for the authenticated screens (locked with Ben
 * via mockup, 2026-06-17).
 *
 *   Row 1 (brand): AvSuite logo pinned top-LEFT, Flight Standards logo pinned
 *                  top-RIGHT (space-between). Dark variants on a dark surface.
 *   Row 2 (controls): the page's existing header controls, horizontally CENTRED in a
 *                  slightly lighter sub-bar. Rendered only when `children` are passed;
 *                  wraps gracefully on narrow/tablet widths.
 *
 * Each page passes its own controls (nav links, etc.) as children so placement is
 * identical everywhere. The global ☰ menu + Home button (AppMenu) sit at the far-left
 * of Row 1, so every authenticated page gets consistent navigation (S48).
 */
import { type CSSProperties } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppMenu, { type MenuExtraItem } from './AppMenu';
import ReportProblemButton from './ReportProblemButton';
import NotificationBell from './ops-board/NotificationBell';
import { useNotifications } from '../context/NotificationsContext';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../lib/useIsMobile';
import type { AppNotification } from '../lib/conflicts';

type Props = {
  /** Controls for the centred Row 2. Omit for a brand-only header. */
  children?: React.ReactNode;
  /** Optional label shown subtly next to the AvSuite mark (e.g. "Admin", "Account"). */
  context?: string;
  /**
   * Optional: called after the ☰ "New student / client" create succeeds, so the hosting page can
   * refresh its people reference data (e.g. OpsBoard.loadRefData) and show the new person in
   * pickers without a full reload. Threaded down to AppMenu. Omit on pages that fetch on mount.
   */
  onPeopleChanged?: () => void;
  /** Mobile-only extra ☰ items (e.g. Ops Board Beta/Offline). Threaded to AppMenu; no desktop effect. */
  menuExtras?: MenuExtraItem[];
};

const BRAND_BG = '#0f172a';
const CONTROLS_BG = '#1e293b';
const headerBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)',
  borderRadius: 5, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', lineHeight: 1.4,
};

// ── Mobile brand-row primitives (2026-07-23 header rework) ─────────────────────
// 44×44 hit area (touch minimum) drawing a compact 34×34 visual box — the bar stays dense
// while every icon-only control keeps a full-size touch target.
const mobileHit: CSSProperties = {
  width: 44, height: 44, padding: 0, background: 'transparent', border: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  flexShrink: 0, textDecoration: 'none',
};
const mobileBox: CSSProperties = {
  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: 8, color: 'rgba(255,255,255,0.9)',
};
// Line-icon stroke style shared by the mobile utility glyphs (person, doorway). No icon library
// is installed (project convention), so these are tiny inline SVGs matching the approved mockup.
const icStroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const PersonIcon = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true" style={{ display: 'block' }}>
    <circle cx="10" cy="7" r="3" {...icStroke} />
    <path d="M4.5 16c0-3 2.5-5 5.5-5s5.5 2 5.5 5" {...icStroke} />
  </svg>
);
const SignOutIcon = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true" style={{ display: 'block' }}>
    <path d="M11 3H5v14h6" {...icStroke} />
    <path d="M9 10h8" {...icStroke} />
    <path d="M14.5 7l3 3-3 3" {...icStroke} />
  </svg>
);

export default function AppHeader({ children, context, onPeopleChanged, menuExtras }: Props) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { logout } = useAuth();
  // Global notifications bell — single shared source (polls once for the whole app, not per page).
  const { notifications, unread, markRead } = useNotifications();

  // Route a clicked notice by type, then mark it read. Conflict/task → Ops Board (the resolver
  // opens via ?conflict=); enquiry_escalation/enquiry_assigned → the enquiry deep-link.
  const onActivate = (n: AppNotification) => {
    void markRead(n.id);
    if (n.enquiry_id) navigate(`/enquiries/${n.enquiry_id}`);
    else if (n.conflict_item_id) navigate(`/ops-board?conflict=${n.conflict_item_id}`);
    else if (n.task_id) navigate('/ops-board');
  };

  const signOut = async () => { await logout(); navigate('/login', { replace: true }); };

  return (
    // Sticky top-0 so the header stays visible while the page scrolls (S-item-4). z-index 100 sits
    // above page/board content but BELOW every fixed modal overlay (BookingDetail 200, ModalOverlay
    // 300, page modals 1000+). On the Ops Board (a 100dvh flex-column that scrolls its board area
    // internally, not the document) sticky is inert — the header is pinned by the layout there.
    <header style={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Row 1 — brand. Mobile (≤768px, 2026-07-23 rework): ONE compact bar — ☰ + AvSuite mark
          left, the four utility actions as icons nestled left of the Flight Standards logo at the
          far right (Home lives in the ☰ menu on mobile). flexWrap lets the icon cluster drop to a
          second slim line on very narrow phones (~360px) rather than crowd the logos.
          Desktop (≥769px): the mockup-locked two-cluster layout, untouched. */}
      {isMobile ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap',
            rowGap: 2, columnGap: 4, padding: '4px 8px',
            background: BRAND_BG, borderBottom: '1px solid #1e293b',
          }}
        >
          <AppMenu onPeopleChanged={onPeopleChanged} extraItems={menuExtras} />
          <img
            src="/brand/avsuite-wing-amber-dark.svg"
            alt="AvSuite"
            style={{ height: 22, width: 'auto', display: 'block' }}
          />
          {context && (
            <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {context}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {/* Utility icon cluster (all four remain in the ☰ menu too). The bell keeps its own
              (unchanged) component; the others are icon variants of the same actions. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <ReportProblemButton iconOnly />
            <NotificationBell notifications={notifications} unread={unread} onActivate={onActivate} />
            <Link to="/profile" title="Profile" aria-label="Profile" style={mobileHit}>
              <span style={mobileBox}><PersonIcon /></span>
            </Link>
            <button type="button" onClick={signOut} title="Sign out" aria-label="Sign out" style={mobileHit}>
              <span style={mobileBox}><SignOutIcon /></span>
            </button>
          </div>
          {/* marginLeft:auto keeps the FS logo pinned to the right edge even when the flexWrap
              drops it (with the icon cluster) to a second line on very narrow phones. */}
          <img
            src="/brand/flight-standards-logo-dark.svg"
            alt="Flight Standards"
            style={{ height: 30, width: 'auto', display: 'block', marginLeft: 'auto' }}
          />
        </div>
      ) : (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 8,
          columnGap: 12,
          padding: '10px 18px',
          background: BRAND_BG,
          borderBottom: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
          <AppMenu onPeopleChanged={onPeopleChanged} />
          <img
            src="/brand/avsuite-wing-amber-dark.svg"
            alt="AvSuite"
            style={{ height: 30, width: 'auto', display: 'block' }}
          />
          {context && (
            <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {context}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', columnGap: 10, rowGap: 8 }}>
          <ReportProblemButton />
          {/* Global header cluster (S48): notifications bell + Profile + Sign out, visible without
              opening the ☰ (Profile + Sign out remain in the ☰ menu too — no avatar dropdown). */}
          <NotificationBell notifications={notifications} unread={unread} onActivate={onActivate} />
          <Link to="/profile" title="Your account" style={headerBtn}>Profile</Link>
          <button type="button" onClick={signOut} style={{ ...headerBtn, fontFamily: 'inherit' }}>Sign out</button>
          <img
            src="/brand/flight-standards-logo-dark.svg"
            alt="Flight Standards"
            style={{ height: 34, width: 'auto', display: 'block' }}
          />
        </div>
      </div>
      )}

      {/* Row 2 — controls, centred (omitted entirely when there are no controls) */}
      {children && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 10,
            padding: '8px 18px',
            background: CONTROLS_BG,
            borderBottom: '1px solid #334155',
          }}
        >
          {children}
        </div>
      )}
    </header>
  );
}
